import {
    Component,
    ElementRef,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    Device,
    Gateway,
    Installation,
    PaginationInfo,
    ParkScheduleLog,
    ParkCommandLog,
} from './scheduling-assets/scheduling-model';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatDialog, MatDialogActions, MatDialogContent, MatDialogClose } from '@angular/material/dialog';
import { SchedulingService } from 'src/shared/services/scheduling.service';
import Swal from 'sweetalert2';
import { finalize, switchMap, take, tap } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ErrorLibService } from 'src/shared/services/error-lib.service';
import { tzConvertUTC2Local, tzConvertISOStartDate, tzConvertISOEndDate } from 'src/assets/convertTimeZone/convertTimeZone';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Imports Standalone
import { PageHeaderComponent } from 'src/shared/components/page-header/page-header.component';
import { TableHeaderComponent } from 'src/shared/helper-components/table-header/table-header.component';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { FileUploadModule } from 'primeng/fileupload';
import { TagModule } from 'primeng/tag';
import { MatStepperModule } from '@angular/material/stepper';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { DialogModule } from 'primeng/dialog';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSliderModule } from '@angular/material/slider';
import { MatButtonModule } from '@angular/material/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DatePickerModule } from 'primeng/datepicker';
@Component({
    selector: 'app-scheduling',
    templateUrl: './scheduling.component.html',
    styleUrl: './scheduling.component.less',
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        PageHeaderComponent,
        TableHeaderComponent,
        ReactiveFormsModule,
        MatStepperModule,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        MatIcon,
        MatProgressBarModule,
        MatSelectModule,
        MatDialogContent,
        MatChipsModule,
        MatRadioModule,
        MatSliderModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        NgxMaterialTimepickerModule,
        DialogModule,
        MatDialogActions,
        FormsModule,
        TableModule,
        ButtonModule,
        TooltipModule,
        InputIconModule,
        CardModule,
        FileUploadModule,
        IconFieldModule,
        TagModule,
        TranslateModule,
        MatDialogClose,
        DatePickerModule
    ]
})

export class SchedulingComponent {
    constructor(
        private formBuilder: FormBuilder,
        public dialog: MatDialog,
        public schedulingService: SchedulingService,
        private errorLibService: ErrorLibService,
        private translate: TranslateService
    ) { }

    @ViewChild('dialogTemplate') dialogTemplate: TemplateRef<any>;

    /** Utilizado para acessar o campo de input dos arquivos **/
    @ViewChild('fileUploadSimple') fileUploadSimple: ElementRef;

    public createSchedulingBox: boolean = false;
    public filterSchedulingBox: boolean = false;

    /** controlador do box de criação **/
    public createSchedulingBoxOpen() {
        this.createSchedulingBox = !this.createSchedulingBox;
        this.filterSchedulingBox = false;
    }

    /** Controlador do box de filtro. **/
    public filterSchedulingBoxOpen() {
        this.filterSchedulingBox = !this.filterSchedulingBox;
        this.createSchedulingBox = false;
    }

    /** Armazena os dados do formulário de criação de agendamentos **/
    public schedulingForm: FormGroup = this.formBuilder.group({
        installationReference: [null, Validators.required],
        installationSelected: [null, Validators.required],
        gatewayReference: [null, Validators.required],
        gatewaySelected: [null, Validators.required],
    });

    /** Utilizado apenas para desabilitar a aba de criar agendamentos caso o usuário não tenha selecionado as instalações ou gateways **/
    public createSchedulingForm: FormGroup = this.formBuilder.group({});

    /** Armazena os dados do formulário de filtro de agendamentos **/
    public filterSchedulingForm: FormGroup = this.formBuilder.group({
        status: [null],
        requestId: [null],
        createdBy: [null],
        dateGte: [null],
        dateLte: [null],
    });

    /** Lista que armazena os dados da tabela **/
    public ELEMENT_DATA: ParkScheduleLog[] = [];

    /** Proproedades utilizadas para geração e manipulação dos arquivos PDF e CSV **/
    public FILE_DATA: ParkScheduleLog[] = [];
    public isReady = false;
    public isFileLoading: boolean = false;

    /** Propriedades do paginator **/
    public paginationProperties: PaginationInfo = new PaginationInfo(
        0,
        0,
        false,
        false,
        null,
        null
    );

    /** Propriedades utilizadas na paginação **/
    public currentPage: number = 0;
    public pageIndex: number = 0;
    public pageSize: number = 10;
    public length: number = 0;
    public previousPageIndex: number = 0;
    public first: number = 0;

    /** Variáveis utilizadas para manipulação do Paginator **/
    @ViewChild(MatPaginator) paginator: MatPaginator;

    /** Função chamada toda vez que ocorre um evento no paginator **/
    public pageChanged(event: any) {
        if (event.first > this.first && this.paginationProperties.hasNextPage) {
            this.first = event.first;
            this.filterScheduling(this.paginationProperties.endCursor);
        } else if (
            event.first < this.first &&
            this.paginationProperties.hasPreviousPage
        ) {
            this.first = event.first;
            this.filterScheduling(this.paginationProperties.startCursor);
        }
    }

    public dataSource = new MatTableDataSource<ParkScheduleLog>(this.ELEMENT_DATA);

    /** Atualiza a tabela **/
    public setDataSourceAttributes() {
        this.dataSource.data = this.ELEMENT_DATA;
    }

    /** Propriedade que configura as colunas da tabela **/
    public displayedColumns = [
        { field: 'switchOnTime', header: 'Ligar' },
        { field: 'switchOffTime', header: 'Desligar' },
        { field: 'dimming', header: 'Dimerização' },
        { field: 'status', header: 'Status' },
        { field: 'totalInstallations', header: 'Instalações' },
        { field: 'successCount', header: 'Sucesso' },
        { field: 'failCount', header: 'Falha' },
        { field: 'createdBy', header: 'Criado Por' },
        { field: 'createdAt', header: 'Criado em' },
        { field: 'actions', header: 'Ações' },
    ];

    /** Propriedade que manipula qual o tipo de dialog será exibido para mostrar as instalações/gateways selecionados **/
    public selectedDialogType: 'installation' | 'gateway' | null = null;
    /** Armazena a lista de logs de comando **/
    public logsSchedulingList: ParkCommandLog[] = [];

    /** Estado de paginação/filtro do card de command logs **/
    public commandLogsRequestId: string = '';
    public commandLogsStatusFilter: string | null = null;
    public commandLogsMacFilter: string = '';
    public commandLogsPageSize: number = 10;
    public commandLogsPage: number = 1;
    public commandLogsPagination: PaginationInfo = new PaginationInfo(
        0,
        0,
        false,
        false,
        null,
        null
    );
    public isLoadingCommandLogs: boolean = false;
    /** Armazena a lista de instalações para a criação de agendamentos **/
    public installationList: Installation[] = [];
    /** Armazena a lista de gateways para a criação de agendamentos **/
    public gatewayList: Gateway[] = [];
    /** Armazena as instalações selecionadas pelo usuário **/
    public installationsSelected: any[] = [];
    /** Armazena os gateways selecionados pelo usuário **/
    public gatewaysSelected: any[] = [];
    /** Propriedade que manipula a mensagem exibida caso nenhum gateway ou instalação sejam encontrados no arquivo de importação **/
    public installationsAndGatewaysNotFound: boolean = false;

    /**
     * Propriedades de loading
     * **/
    public isInstallationLoadingCreateScheduling: boolean = false;
    public isGatewaysLoadingFilter: boolean = false;
    public isLoadingFilterScheduling: boolean = false;
    public isLoadingCreateSchedule: boolean = false;
    public isLoadingImportFilter: boolean = false;
    /** Propriedade que controla quando o loading do parque todo será exibido **/
    public isLoadingAllInstallations: boolean = false;

    /** Propriedade que manipula quando o dialog de logs de agendamento será exibido **/
    public isLogsSchedulingDialogOpen: boolean = false;

    /** Propriedade que manipula o botão de importação **/
    public isDisabledImportButton: boolean = true;

    /** Propriedades do formulário unificado de criação **/
    /** Horário de ligar (HH:MM) **/
    public switchOnTime: string = '00:00';
    /** Horário de desligar (HH:MM) **/
    public switchOffTime: string = '00:00';
    /** Nível de dimerização (0-100) **/
    public dimmingValue: number = 50;
    /** Slot de agendamento (1-4) **/
    public scheduleSlot: number = 1;
    /** Canal (1-255) **/
    public channel: number = 1;

    /** Propriedades utilizadas para manipulação dos arquivos de importação **/
    public dataFile: string;
    public dataToImport: Array<any> = [];

    /** Propriedade que manipula quando o parque todo estiver selecionado **/
    public fullCoverageIsChecked: boolean = false;

    /** Abre o modal que exibe as instalações ou gateways selecionados pelo usuário **/
    public openInstallationsGatewaysDialog(
        type: 'installation' | 'gateway'
    ): void {
        this.selectedDialogType = type;
        this.dialog.open(this.dialogTemplate);
    }

    /** Evento disparado quando o usuário selecionar a opção do parque todo **/
    public setFullCoverage(event: any): void {
        /** Caso o usuário clique na opção de aplicar para o parque todo **/
        if (event) {
            /** Inverte o valor da propriedade para cada vez que o usuário clicar no botão (mantendo sempre atualizado) **/
            this.fullCoverageIsChecked = !this.fullCoverageIsChecked;
            /** Reseta o formulário para seleção de instalações e gateways **/
            this.schedulingForm.reset();
            /** Limpa a lista de instalações caso alguma instalação já tenha sido selecionada **/
            this.installationsSelected = [];
            /** Limpa a lista de gateways caso algum gateway já tenha sido selecionado **/
            this.gatewaysSelected = [];

            /** Caso o usuário selecione a opção para ativar o parque todo **/
            if (this.fullCoverageIsChecked == true) {
                /** Desabilita a seleção de instalações e gateways **/
                this.schedulingForm.disable();
                /** Caso o usuário clique novamente na opção do parque todo (desativando) **/
            } else {
                /** Habilia a seleção de instalações e gateways novamente **/
                this.schedulingForm.enable();
            }
        }
    }

    /** Salva as instalações selecionadas pelo usuário e remove as desmarcadas da lista de instalações selecionadas **/
    public saveInstallationsSelected(
        event: MatOptionSelectionChange,
        installation: Installation
    ) {
        /** Verifica se a instalação foi selecionada pelo usuário **/
        if (event.source.selected) {
            /** Adiciona a instalação em uma lista de instalações selecionadas **/
            this.installationsSelected.push(installation);

            /** Remove o validator do campo de seleção dos gateways (garantindo que pelo menos a instalação esteja selecionada) **/
            this.schedulingForm.get('gatewaySelected')?.clearValidators();
            this.schedulingForm.get('gatewaySelected')?.updateValueAndValidity();
            /** Caso o usuário desmarque uma opção que estava selecionada **/
        } else {
            /** Cria index para remover a instalação da lista **/
            const index = this.installationsSelected.indexOf(installation);

            /** Verifica se é o index é válido (não é menor que 0) **/
            if (index > -1) {
                /** Remove a instalação da lista passando o index da instalação que está sendo desmarcada **/
                this.installationsSelected.splice(index, 1);

                /** Caso nenhuma instalação tenha sido selecionada **/
                if (this.installationsSelected.length < 1) {
                    /** Adiciona o validator para o gateway, garantindo que o usuário selecione ao menos um gateway/instalação **/
                    this.schedulingForm
                        .get('gatewaySelected')
                        ?.setValidators([Validators.required]);
                    this.schedulingForm.get('gatewaySelected')?.updateValueAndValidity();
                }
            }
        }
    }

    /** Remove a instalação no modal de instalações selecionadas **/
    public removeInstallationOption(installation: any) {
        /** Cria o index para acessar a instalação a ser removida **/
        const index = this.installationsSelected.indexOf(installation);

        /** Verifica se é um index válido ou seja, se não é menor que indice inicial 0 **/
        if (index >= 0) {
            /** Remove a instalação da lista **/
            this.installationsSelected.splice(index, 1);
        }

        /** Utilizado para atualizar as instalações removidas do select também **/
        const selectedIds = this.installationList
            /** Filtra apenas as instalações que ainda estão marcadas e que não foram removidas **/
            .filter((installation) =>
                this.installationsSelected.includes(installation)
            )
            .map((installation) => installation.id);

        /** Atualiza o campo do formulário com as instalações que deverão estar selecionadas ou não **/
        this.schedulingForm.get('installationSelected')?.setValue(selectedIds);
    }

    /** Salva os gateways selecionados pelo usuário e remove os desmarcados da lista de gateways selecionados **/
    public saveGatewaysSelected(
        event: MatOptionSelectionChange,
        gateway: Gateway
    ) {
        /** Verifica se o gateway foi selecionado pelo usuário **/
        if (event.source.selected) {
            /** Adiciona o gateway em uma lista de gateways selecionados **/
            this.gatewaysSelected.push(gateway);

            /** Remove o validator do campo de seleção das instalações (garantindo que pelo menos o gateway esteja selecionado)**/
            this.schedulingForm.get('installationSelected')?.clearValidators();
            this.schedulingForm.get('installationSelected')?.updateValueAndValidity();
            /** Caso o usuário desmarque uma opção que estava selecionada **/
        } else {
            /** Cria index para remover o gateway da lista **/
            const index = this.gatewaysSelected.indexOf(gateway);

            /** Verifica se é o index é válido (não é menor que 0) **/
            if (index > -1) {
                /** Remove o gateway da lista passando o index da gateway que está sendo desmarcado **/
                this.gatewaysSelected.splice(index, 1);

                /** Caso nenhum gateway tenha sido selecionado **/
                if (this.gatewaysSelected.length < 1) {
                    /** Adiciona o validator para a instalação, garantindo que o usuário selecione ao menos um gateway/instalação **/
                    this.schedulingForm
                        .get('installationSelected')
                        ?.setValidators([Validators.required]);
                    this.schedulingForm
                        .get('installationSelected')
                        ?.updateValueAndValidity();
                }
            }
        }
    }

    /** Remove o gateway no modal de gateway selecionados **/
    public removeGatewayOption(gateway: string) {
        /** Cria o index para acessar o gateway a ser removido **/
        const index = this.gatewaysSelected.indexOf(gateway);

        /** Verifica se é um index válido ou seja, se não é menor que indice inicial 0 **/
        if (index >= 0) {
            /** Remove o gateway da lista **/
            this.gatewaysSelected.splice(index, 1);
        }
        /** Utilizado para atualizar os gateways removidos do select também **/
        const selectedIds = this.gatewayList
            /** Filtra apenas os gateways que ainda estão marcadas e que não foram removidos **/
            .filter((gateway) => this.gatewaysSelected.includes(gateway.reference))
            .map((gateway) => gateway.id);
        /** Atualiza o campo do formulário com os gateways que deverão estar selecionados ou não **/
        this.schedulingForm.get('gatewaySelected')?.setValue(selectedIds);
    }

    /** Método responsável por filtrar as instalações por referência (select de instalações) **/
    public filterInstallations(reference: any) {
        /** Remove todos os validadores de gateways e instalações **/
        this.schedulingForm.get('installationReference')?.clearValidators();
        this.schedulingForm.get('installationReference')?.updateValueAndValidity();
        this.schedulingForm.get('gatewayReference')?.clearValidators();
        this.schedulingForm.get('gatewayReference')?.updateValueAndValidity();

        /** Ativa o loading de carregamento das instalações **/
        this.isInstallationLoadingCreateScheduling = true;
        /** Limpa a lista de instalações para não duplicar dados **/
        this.installationList = [];
        /** Limpa as instalações já selecionadas **/
        this.schedulingForm.value.installationSelected = null;

        /** Realiza a requisição de filtro das instalações **/
        this.schedulingService
            .getInstallationsByReference(reference)
            .subscribe({
                next: (response: any) => {
                    response?.data?.installation.edges.forEach((installation: any) => {
                        /** Verificação para instalações já selecionadas **/
                        const selectedInstallations = this.installationsSelected.filter(
                            (installationRef) =>
                                installationRef.reference == installation?.node?.reference
                        );

                        /** Remove as instalações já selecionadas pelo usuário para não duplicar a 
                         * lista na hora de enviar os dados para o agendamento **/
                        if (selectedInstallations) {
                            this.installationsSelected.splice(
                                this.installationsSelected.indexOf(0)
                            );
                        }

                        /** Verificação para adicionar na lista apenas as instalações com gateways vinculados **/
                        if (
                            installation?.node?.gateway !== null &&
                            installation?.node?.gateway !== undefined
                        ) {
                            /** Preenche a lista de instalações apenas para instalações 
                             * que possuem gateways e equipamentos vinculados **/
                            this.installationList.push(
                                new Installation(
                                    installation?.node?.reference,
                                    installation?.node?.id,
                                    new Device(
                                        installation?.node?.device?.id,
                                        installation?.node?.device?.macAddress
                                    ),
                                    new Gateway(
                                        installation?.node?.gateway?.reference,
                                        installation?.node?.gateway?.id,
                                        installation?.node?.gateway?.device?.serialNumber
                                    )
                                )
                            );
                        }
                    });

                    /** Desativa o loading de carregamento **/
                    this.isInstallationLoadingCreateScheduling = false;

                    /** Caso nenhuma instalação seja retornada no filtro **/
                    if (this.installationList.length === 0) {
                        /** Exibe alerta ao usuário **/
                        Swal.fire({
                            title: 'Sua busca não retornou resultados',
                            text: 'Nenhum resultado para este filtro',
                            icon: 'warning',
                            confirmButtonText: 'Ok',
                        });
                        /** Desativa o loading de carregamento **/
                        this.isInstallationLoadingCreateScheduling = false;
                    }
                },
                /** Caso ocorra algum erro na requisição **/
                error: (error: any) => {
                    /** Exibe um alerta com o erro na tela **/
                    this.errorLibService.errorAlert(error);
                    /** Exibe o erro no console do navegador **/
                    console.error('Error on a filter installations', error);
                    /** Desativa o loading de carregamento **/
                    this.isInstallationLoadingCreateScheduling = false;
                },
            });
    }

    /** Método responsável por filtrar os gateways por referência (select de gateways) **/
    public filterGateways(reference: any) {
        /** Remove todos os validadores de gateways e instalações **/
        this.schedulingForm.get('gatewayReference')?.clearValidators();
        this.schedulingForm.get('gatewayReference')?.updateValueAndValidity();
        this.schedulingForm.get('installationReference')?.clearValidators();
        this.schedulingForm.get('installationReference')?.updateValueAndValidity();

        /** Ativa o loading de carregamento **/
        this.isGatewaysLoadingFilter = true;
        /** Limpa a lista de gateways para evitar duplicação de dados**/
        this.gatewayList = [];
        /** Limpa os gateways já selecionados **/
        this.schedulingForm.value.gatewaySelected = null;

        /** Realiza a requisição de filtro dos gateways **/
        this.schedulingService
            .getGatewaysByReference(reference)
            .subscribe({
                next: (response: any) => {
                    response?.data?.gatewayInstallation.edges.forEach((gateway: any) => {
                        /** Verificação para gateways já selecionados **/
                        const selectedGateways = this.gatewaysSelected.filter(
                            (gatewayRef) => gatewayRef.reference == gateway?.node?.reference
                        );

                        /** Remove os gateways já selecionados pelo usuário para não duplicar a lista na hora de enviar os dados para o agendamento **/
                        if (selectedGateways) {
                            this.gatewaysSelected.splice(this.gatewaysSelected.indexOf(0));
                        }

                        /** Preenche a lista de gateways **/
                        this.gatewayList.push(
                            new Gateway(
                                gateway?.node?.reference,
                                gateway?.node?.id,
                                gateway?.node?.device?.serialNumber
                            )
                        );
                    });

                    /** Desativa o loading de carregamento **/
                    this.isGatewaysLoadingFilter = false;

                    /** Caso nenhum gateway seja retornado no filtro **/
                    if (this.gatewayList.length === 0) {
                        /** Exibe alerta ao usuário **/
                        Swal.fire({
                            title: 'Sua busca não retornou resultados',
                            text: 'Nenhum resultado para este filtro',
                            icon: 'warning',
                            confirmButtonText: 'Ok',
                        });
                        /** Desativa o loading de carregamento **/
                        this.isGatewaysLoadingFilter = false;
                    }
                },
                /** Caso ocorra algum erro na requisição **/
                error: (error: any) => {
                    /** Exibe um alerta com o erro na tela **/
                    this.errorLibService.errorAlert(error);
                    /** Exibe o erro no console do navegador **/
                    console.error('Error on a filter gateways', error);
                    /** Desativa o loading de carregamento **/
                    this.isGatewaysLoadingFilter = false;
                },
            });
    }

    /** Método que lê o arquivo CSV para que possa ser filtrado as instalações e gateways */
    public async getTextFromFile(event: any) {
        /** Ativa o Loading */
        this.isLoadingImportFilter = false;

        /** Habilita o botão de importação */
        this.isDisabledImportButton = false;

        /** Constante do tipo File */
        const file: File = event.target.files[0];

        let fileContent = await file.text();

        /** Propriedade que recebe os dados do arquivo  */
        this.dataFile = fileContent;
    }

    /** Método que realiza o filtro das instalações e gateways a partir da leitura de um arquivo CSV anexado pelo usuário */
    public async filterInstallationsAndGatewaysFile() {
        /** Ativa o loading da tela */
        this.isLoadingImportFilter = true;

        /** Oculta a mensagem de alerta para lista de instalações/gateways vazias **/
        this.installationsAndGatewaysNotFound = false;

        /** Armazena os dados do CSV */
        this.dataToImport = this.schedulingService.importDataFromCSV(this.dataFile);

        /** Armazena o objeto que contém a propriedade Reference que representa a referência da instalação */
        const referenceInstallationAndGateway: any = this.dataToImport.map(
            (row) => ({
                installation: row?.installation,
                gateway: row?.gateway,
            })
        );

        /** Para cada elemento do arquivo */
        for (const data of referenceInstallationAndGateway) {
            /** Caso o cabeçalho seja inválido */
            if (!data.installation && !data.gateway) {
                /** exibe um alerta ao usuário */
                await Swal.fire({
                    title: 'Cabeçalho ou formato de arquivo incorreto',
                    text: 'Por favor, verifique o arquivo e tente novamente',
                    icon: 'warning',
                    confirmButtonText: 'Ok',
                });
                this.isLoadingImportFilter = false;
                return;
            }

            /** Constante que armazena as referências das instalações **/
            const referenceInstallations = data?.installation;

            /** Constante que armazena as referências dos gateways **/
            const referenceGateways = data?.gateway;

            /** Realiza a requisição pra filtrar os gateways pela referência do gateway presente no arquivo **/
            this.schedulingService
                .getGatewaysByReference(referenceGateways)
                .pipe(
                    take(1),
                    tap((res: any) => {
                        const gatewayReference: Gateway[] =
                            (res?.data?.gatewayInstallation?.edges ?? [])
                                .map((edge: any) => edge?.node)
                                .filter(
                                    (gateway: Gateway) => gateway?.reference === referenceGateways
                                ); /// Compara a referência do arquivo com a referência da api

                        /** Caso a referência do arquivo exista na api **/
                        if (gatewayReference.length > 0) {
                            res?.data?.gatewayInstallation?.edges?.forEach((gateway: any) => {
                                /** Preenche a lista de gateways **/
                                this.gatewayList.push(
                                    new Gateway(
                                        gateway?.node?.reference,
                                        gateway?.node?.id,
                                        gateway?.node?.device?.serialNumber
                                    )
                                );
                            });

                            /** Armazena o id do gateway **/
                            const fileGatewaysId = this.gatewayList.map(
                                (gateway) => gateway?.id
                            );

                            /** Armazena a referência do gateway **/
                            const fileGatewaysReference = this.gatewayList.map(
                                (gateway) => gateway?.reference
                            );

                            /** Marca os gateways encontrados a partir do id no 
                             * campo do formulário de gateways selecionados **/
                            this.schedulingForm
                                .get('gatewaySelected')
                                ?.setValue(fileGatewaysId);

                            /** Marca os gateways encontrados a partir da referência no 
                             * campo do formulário de gateways **/
                            this.schedulingForm
                                .get('gatewayReference')
                                ?.setValue(fileGatewaysReference);
                        }
                    }),
                    /** Realiza a requisição pra filtrar as instalações 
                     * pela referência da instalação presente no arquivo **/
                    switchMap(() =>
                        this.schedulingService
                            .getInstallationsByReference(referenceInstallations)
                            .pipe(take(1))
                    ),
                    /** Manipulação dos dados da resposta da requisição **/
                    tap((res: any) => {
                        const installationReference: Installation[] =
                            (res?.data?.installation?.edges ?? [])
                                .map((edge: any) => edge.node)
                                .filter(
                                    (installation: Installation) =>
                                        installation?.reference === referenceInstallations
                                ); /// Compara a referência do arquivo com a referência da api

                        /** Caso a referência do arquivo exista na api **/
                        if (installationReference.length > 0) {
                            res?.data?.installation?.edges?.forEach((installation: any) => {
                                /** Preenche a lista de instalações **/
                                this.installationList.push(
                                    new Installation(
                                        installation?.node?.reference,
                                        installation?.node?.id,
                                        new Device(
                                            installation?.node?.device?.id,
                                            installation?.node?.device?.macAddress
                                        ),
                                        new Gateway(
                                            installation?.node?.gateway?.reference,
                                            installation?.node?.gateway?.id,
                                            installation?.node?.gateway?.device?.serialNumber
                                        )
                                    )
                                );
                            });

                            /** Armazena o id da instalação **/
                            const fileInstallationsId = this.installationList.map(
                                (installation) => installation?.id
                            );

                            /** Armazena a referência da instalação **/
                            const fileInstallationsReference = this.installationList.map(
                                (installation) => installation?.reference
                            );

                            /** Marca as instalações encontradas a partir do id no 
                             * campo do formulário de instalações selecionadas **/
                            this.schedulingForm
                                .get('installationSelected')
                                ?.setValue(fileInstallationsId);

                            /** Marca as instalações encontradas a partir da referência no 
                             * campo do formulário de instalações **/
                            this.schedulingForm
                                .get('installationReference')
                                ?.setValue(fileInstallationsReference);
                        }
                    }),
                    finalize(() => {
                        /** Desativa o loading na tela **/
                        this.isLoadingImportFilter = false;

                        /** Habilita o botão de importação novamente **/
                        this.isDisabledImportButton =
                            this.installationList.length > 0 || this.gatewayList.length > 0;
                        this.fileUploadSimple.nativeElement.value = null;

                        /** Caso nenhum gateway ou instalação seja encontrado **/
                        if (
                            this.installationList.length === 0 &&
                            this.gatewayList.length === 0
                        ) {
                            /** Exibe mensagem na tela que não foram encontrados 
                             * gateways/instalações válidas no arquivo **/
                            this.installationsAndGatewaysNotFound = true;
                        }
                    })
                )
                .subscribe({
                    error: (error: any) => {
                        this.errorLibService.errorAlert(error);
                        console.error('Error on import installations/gateways file', error);
                        this.isLoadingImportFilter = false;
                    },
                });
        }
    }

    /** Método que realiza o download de um arquivo csv que contém o cabeçalho com o modelo para os usuários **/
    public downloadCsvFile(): void {
        const headers: string[] = ['installation', 'gateway']; //Define o cabeçalho do arquivo csv
        const fileName = 'schedulings.csv'; //Nome do arquivo
        const csvContent = headers.join(';') + '\n'; // Gera o conteúdo do CSV como string, separado por ponto e vírgula
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }); // Cria um objeto Blob com o conteúdo do CSV e define o tipo MIME
        const link = document.createElement('a'); // Cria um elemento <a> dinamicamente
        const url = URL.createObjectURL(blob); // Cria uma URL para o Blob
        link.setAttribute('href', url); // Define o atributo href do link com a URL do Blob
        link.setAttribute('download', fileName); // Define o atributo download do link com o nome do arquivo
        link.style.visibility = 'hidden'; // Define a visibilidade do link como 'hidden' para que ele não apareça na página
        document.body.appendChild(link); // Adiciona o link ao corpo do documento
        link.click(); // Simula um clique no link para iniciar o download do arquivo
        document.body.removeChild(link); // Remove o link do documento após o clique
    }

    /**
     * Monta a lista de MACs alvo do agendamento a partir da seleção atual.
     * - Parque todo → retorna `null` (não envia macAddresses).
     * - Instalações selecionadas → `installation.device.macAddress`.
     * - Gateways selecionados → resolve todas as instalações de cada
     *   concentrador via getAllMacAddressesByGateway (percorre as páginas
     *   pelo cursor; sem isso o parque do concentrador seria cortado no
     *   tamanho de página e o agendamento sairia incompleto em silêncio).
     * Deduplica e remove valores falsy.
     *
     * Uma seleção que resolva para lista vazia é devolvida como [] e não
     * como null, porque null significa parque todo para o park-scheduler.
     **/
    public async buildMacAddresses(): Promise<string[] | null> {
        /** Parque todo → omitir macAddresses **/
        if (this.fullCoverageIsChecked) {
            return null;
        }

        /** Lista acumuladora de MACs **/
        const macAddresses: string[] = [];

        /** MACs das instalações selecionadas diretamente **/
        this.installationsSelected?.forEach((installation: Installation) => {
            const mac = installation?.device?.macAddress;
            if (mac) {
                macAddresses.push(mac);
            }
        });

        /** MACs das instalações resolvidas a partir dos gateways selecionados **/
        const gatewayIds: any[] = this.schedulingForm?.value?.gatewaySelected || [];

        /** Resolve os concentradores em paralelo, cada um percorrendo todas as suas páginas **/
        const gatewayMacs = await Promise.all(
            gatewayIds.map((gatewayId: any) =>
                this.schedulingService.getAllMacAddressesByGateway(gatewayId)
            )
        );

        gatewayMacs.forEach((macs: string[]) => macAddresses.push(...macs));

        /** Deduplica e remove valores falsy **/
        return Array.from(new Set(macAddresses.filter(Boolean)));
    }

    /** Cria o agendamento unificado (ligar + desligar + dimerização) no park-scheduler. **/
    public async createParkSchedule() {
        /** Ativa o loading de carregamento **/
        this.isLoadingCreateSchedule = true;

        try {
            /** Monta a lista de MACs alvo (ou null para parque todo) **/
            const macAddresses = await this.buildMacAddresses();

            /** Seleção que não resolveu nenhuma instalação: aborta.
             * Enviar lista vazia faria o park-scheduler agendar o parque
             * inteiro, porque ausência de MACs significa parque todo lá. **/
            if (macAddresses !== null && macAddresses.length === 0) {
                this.isLoadingCreateSchedule = false;
                Swal.fire({
                    title: this.translate.instant(
                        'scheduling.empty-selection-title'
                    ),
                    text: this.translate.instant(
                        'scheduling.empty-selection-text'
                    ),
                    icon: 'warning',
                    confirmButtonText: 'Ok',
                });
                return;
            }

            /** Converte os horários HH:MM → HH:MM:SS (o backend espera Time) **/
            const onTime = `${this.switchOnTime}:00`;
            const offTime = `${this.switchOffTime}:00`;

            /** Realiza a requisição de criação do agendamento **/
            this.schedulingService
                .createParkSchedule(
                    onTime,
                    offTime,
                    this.dimmingValue,
                    macAddresses,
                    this.scheduleSlot,
                    this.channel
                )
                .subscribe({
                    next: (res: any) => {
                        /** Desativa o loading de carregamento **/
                        this.isLoadingCreateSchedule = false;

                        /** Recupera o requestId retornado **/
                        const requestId =
                            res?.data?.parkScheduleCreate?.schedule?.requestId;

                        /** Exibe alerta de sucesso deixando claro que foi aceito e está em processamento **/
                        Swal.fire({
                            title: 'Agendamento aceito e em processamento.',
                            text: requestId
                                ? `ID da requisição: ${requestId}`
                                : undefined,
                            icon: 'success',
                            confirmButtonText: 'Ok',
                        });

                        /** Atualiza a tabela de requisições **/
                        this.filterScheduling(null);
                    },
                    /** Caso ocorra algum erro na requisição **/
                    error: (err: any) => {
                        /** Exibe um alerta com o erro na tela **/
                        this.errorLibService.errorAlert(err);
                        /** Exibe o erro no console do navegador **/
                        console.error('Error on create parkSchedule mutate', err);
                        /** Desativa o loading de carregamento **/
                        this.isLoadingCreateSchedule = false;
                    },
                });
        } catch (error) {
            /** Erro ao montar os MACs a partir dos gateways **/
            this.errorLibService.errorAlert(error);
            console.error('Error on build macAddresses', error);
            this.isLoadingCreateSchedule = false;
        }
    }

    /** Método que realiza o filtro dos agendamentos **/
    public filterScheduling(cursor: string | null) {
        /** Ativa o loading de carregamento na tela **/
        this.isLoadingFilterScheduling = true;

        if (cursor === null) {
            this.pageIndex = 0;
            this.first = 0;
            this.isReady = true;
        }

        /** Monta os filtros a partir do formulário de filtro **/
        const formValue = this.filterSchedulingForm.value;
        const filters = {
            requestId: formValue?.requestId || null,
            status: formValue?.status || null,
            createdBy: formValue?.createdBy || null,
            dateGte: formValue?.dateGte ? tzConvertISOStartDate(formValue.dateGte) : null,
            dateLte: formValue?.dateLte ? tzConvertISOEndDate(formValue.dateLte) : null,
        };

        /** Realiza a requisição que lista as requisições de agendamento **/
        this.schedulingService
            .getParkScheduleLogs(filters, cursor, this.pageSize)
            .subscribe({
                next: (response: any) => {
                    /** Ignora a emissão de loading (data ainda indefinido) para não
                     * disparar avisos/erros espúrios nem quebrar em data.edges. **/
                    if (response.loading || !response.data?.parkScheduleLogs) {
                        return;
                    }

                    const data = response.data.parkScheduleLogs;

                    this.paginationProperties = new PaginationInfo(
                        data?.count,
                        data?.total,
                        data?.pageInfo?.hasNextPage,
                        data?.pageInfo?.hasPreviousPage,
                        data?.pageInfo?.startCursor,
                        data?.pageInfo?.endCursor
                    );

                    /** Limpa a lista da tabela **/
                    this.ELEMENT_DATA = [];
                    /** Atualiza a quantidade de elementos da tabela **/
                    this.length = data?.total;
                    this.isReady = false;

                    data?.edges?.forEach((edge: any) => {
                        /** Preenche a lista da tabela **/
                        this.ELEMENT_DATA.push(
                            new ParkScheduleLog(
                                edge?.node?.requestId,
                                edge?.node?.createdBy,
                                tzConvertUTC2Local(edge?.node?.createdAt),
                                tzConvertUTC2Local(edge?.node?.updatedAt),
                                edge?.node?.scheduleSlot,
                                edge?.node?.channel,
                                edge?.node?.switchOnTime,
                                edge?.node?.switchOffTime,
                                edge?.node?.dimming,
                                edge?.node?.status,
                                edge?.node?.totalInstallations,
                                edge?.node?.successCount,
                                edge?.node?.failCount
                            )
                        );
                    });

                    /** Atualiza a tabela **/
                    this.setDataSourceAttributes();

                    /** Desativa o loading de carregamento **/
                    this.isLoadingFilterScheduling = false;

                    /** Caso não retorne nenhum dado na lista (já com dados carregados) **/
                    if (data.edges?.length === 0) {
                        /** Exibe alerta ao usuário **/
                        Swal.fire({
                            title: 'Sua busca não retornou resultados',
                            text: 'Nenhum resultado para este filtro',
                            icon: 'warning',
                            confirmButtonText: 'Ok',
                        });

                        /** Desativa o loading de carregamento **/
                        this.isLoadingFilterScheduling = false;
                    }
                },
                error: (error: any) => {
                    /** Exibe um alerta com o erro na tela **/
                    this.errorLibService.errorAlert(error);
                    /** Exibe o erro no console do navegador **/
                    console.error('Error on a getParkScheduleLogs query', error);
                    /** Desativa o loading de carregamento **/
                    this.isLoadingFilterScheduling = false;
                },
            });
    }

    /** Abre o dialog de logs de comando e carrega a 1ª página **/
    public getSchedulingLogs(row: ParkScheduleLog) {
        /** Reseta o estado do card para a requisição selecionada **/
        this.commandLogsRequestId = row.requestId;
        this.commandLogsStatusFilter = null;
        this.commandLogsMacFilter = '';
        this.commandLogsPage = 1;
        this.commandLogsPagination = new PaginationInfo(0, 0, false, false, null, null);
        /** Habilita o dialog de logs **/
        this.isLogsSchedulingDialogOpen = true;
        /** Carrega a primeira página **/
        this.loadCommandLogs(null);
    }

    /** Carrega uma página de logs de comando **/
    public loadCommandLogs(cursor: string | null) {
        this.isLoadingCommandLogs = true;

        const filters = {
            requestId: this.commandLogsRequestId,
            /** Filtro por status **/
            status: this.commandLogsStatusFilter || undefined,
            /** Filtro por MAC address **/
            macAddresses: this.commandLogsMacFilter
                ? [this.commandLogsMacFilter]
                : null,
        };

        this.schedulingService
            .getParkCommandLogs(filters, cursor, this.commandLogsPageSize)
            .subscribe({
                next: (response: any) => {
                    /** Ignora a emissão de loading **/
                    if (response.loading || !response.data?.parkCommandLogs) {
                        return;
                    }

                    const data = response.data.parkCommandLogs;

                    this.commandLogsPagination = new PaginationInfo(
                        data?.count,
                        data?.total,
                        data?.pageInfo?.hasNextPage,
                        data?.pageInfo?.hasPreviousPage,
                        data?.pageInfo?.startCursor,
                        data?.pageInfo?.endCursor
                    );

                    /** Limpa a lista, evitando duplicação de dados **/
                    this.logsSchedulingList = [];

                    data?.edges?.forEach((edge: any) => {
                        /** Preenche a lista dos logs de comando **/
                        this.logsSchedulingList.push(
                            new ParkCommandLog(
                                edge?.node?.requestId,
                                edge?.node?.installationId,
                                edge?.node?.macAddress,
                                edge?.node?.serialNumber,
                                edge?.node?.cmdIndex,
                                edge?.node?.command,
                                edge?.node?.status,
                                tzConvertUTC2Local(edge?.node?.createdAt)
                            )
                        );
                    });

                    this.isLoadingCommandLogs = false;
                },
                /** Caso ocorra algum erro na requisição **/
                error: (error: any) => {
                    /** Exibe um alerta com o erro na tela **/
                    this.errorLibService.errorAlert(error);
                    /** Exibe o erro no console do navegador **/
                    console.error('Error on a getParkCommandLogs query', error);
                    this.isLoadingCommandLogs = false;
                },
            });
    }

    /** Quantidade acumulada de logs percorridos 
     * até a página atual (cresce/diminui ao navegar) **/
    public get commandLogsShownCount(): number {
        return (
            (this.commandLogsPage - 1) * this.commandLogsPageSize +
            this.logsSchedulingList.length
        );
    }

    /** Navegação para a próxima página de command logs **/
    public nextCommandLogsPage() {
        if (this.commandLogsPagination.hasNextPage) {
            this.commandLogsPage++;
            this.loadCommandLogs(this.commandLogsPagination.endCursor);
        }
    }

    /** Navegação para a página anterior de command logs **/
    public prevCommandLogsPage() {
        if (this.commandLogsPagination.hasPreviousPage) {
            this.commandLogsPage--;
            this.loadCommandLogs(this.commandLogsPagination.startCursor);
        }
    }

    /** Aplica os filtros de status/MAC, voltando à 1ª página **/
    public applyCommandLogsFilters() {
        this.commandLogsPage = 1;
        this.commandLogsPagination = new PaginationInfo(0, 0, false, false, null, null);
        this.loadCommandLogs(null);
    }

    /** Limpa os filtros de status/MAC, voltando à 1ª página **/
    public clearCommandLogsFilters() {
        this.commandLogsStatusFilter = null;
        this.commandLogsMacFilter = '';
        this.commandLogsPage = 1;
        this.commandLogsPagination = new PaginationInfo(0, 0, false, false, null, null);
        this.loadCommandLogs(null);
    }

    /** Exclui um agendamento (com confirmação) e recarrega a lista **/
    public deleteScheduling(row: ParkScheduleLog) {
        Swal.fire({
            title: this.translate.instant('scheduling.delete-confirm-title'),
            text: this.translate.instant('scheduling.delete-confirm-text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: this.translate.instant('scheduling.delete-confirm-ok'),
            cancelButtonText: this.translate.instant('scheduling.delete-confirm-cancel'),
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            this.schedulingService.deleteParkSchedule(row.requestId).subscribe({
                next: () => {
                    Swal.fire({
                        icon: 'success',
                        title: this.translate.instant('scheduling.delete-success'),
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    /** Recarrega a lista (reseta first=0) **/
                    this.filterScheduling(null);
                },
                error: (err: any) => this.errorLibService.errorAlert(err),
            });
        });
    }

    /** Método que limpa os dados do filtro de agendamento **/
    public clearSchedulingFilterForm() {
        this.filterSchedulingForm.reset();
        this.length = 0;
    }

    /** Método utilizado para gerar os arquivos csv e pdf da tabela completa **/
    public generateFileData(cursor: string | null, fileType: string) {
        this.isFileLoading = true;
        /** Verifica se a lista está pronta **/
        if (this.isReady == false) {
            this.schedulingService
                .getParkScheduleLogs({}, cursor, 10)
                .subscribe({
                    next: (response: any) => {
                        /** Valida se a resposta está carregando ou não possui dados **/
                        if (response.loading || !response.data) {
                            return;
                        }

                        const data = response?.data?.parkScheduleLogs;

                        data?.edges.forEach((edge: any) => {
                            this.FILE_DATA.push(
                                new ParkScheduleLog(
                                    edge?.node?.requestId,
                                    edge?.node?.createdBy,
                                    tzConvertUTC2Local(edge?.node?.createdAt),
                                    tzConvertUTC2Local(edge?.node?.updatedAt),
                                    edge?.node?.scheduleSlot,
                                    edge?.node?.channel,
                                    edge?.node?.switchOnTime,
                                    edge?.node?.switchOffTime,
                                    edge?.node?.dimming,
                                    edge?.node?.status,
                                    edge?.node?.totalInstallations,
                                    edge?.node?.successCount,
                                    edge?.node?.failCount
                                )
                            );
                        });

                        /** Caso seja a última página a lista está completa **/
                        if (!data?.pageInfo?.hasNextPage) {
                            this.isReady = true;
                        }

                        this.generateFileData(data?.pageInfo?.endCursor, fileType);
                    },
                    error: (error: any) => {
                        this.errorLibService.errorAlert(error);
                    },
                });
        } else {
            this.isFileLoading = false;
            if (fileType === 'PDF') {
                this.downloadPDF();
            } else {
                this.downloadCSV();
            }
        }
    }

    /** Escapa um valor conforme RFC-4180 (aspas dobradas e quoting quando necessário) */
    private csvEscape(value: any): string {
        const s = value === null || value === undefined ? '' : String(value);
        return /[";\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    }

    /** Função que realiza a conversão para arquivo CSV (separador `;`, quoting RFC-4180) */
    public convertToCSV(arr: any[]): string {
        if (!arr || arr.length === 0) {
            return '';
        }
        const headers = Object.keys(arr[0]);
        const headerLine = headers.map((h) => this.csvEscape(h)).join(';');
        const lines = arr.map((obj) =>
            headers.map((h) => this.csvEscape(obj[h])).join(';')
        );
        return [headerLine, ...lines].join('\r\n');
    }

    /** Função que realiza o download dos dados da tabela em CSV */
    public downloadCSV() {
        let schedulingList: any = [];

        this.FILE_DATA.forEach((scheduling) => {
            schedulingList.push({
                Ligar: scheduling?.switchOnTime,
                Desligar: scheduling?.switchOffTime,
                Dimerização: scheduling?.dimming,
                Status: scheduling?.status,
                Instalações: scheduling?.totalInstallations,
                Sucesso: scheduling?.successCount,
                Falha: scheduling?.failCount,
                'Criado por': scheduling?.createdBy,
                'Criado em': scheduling?.createdAt,
            });
        });

        /** Adiciona o conteúdo convertido dentro de uma nova variável. **/
        let data = '﻿' + this.convertToCSV(schedulingList);
        /**  Transforma o conteúdo para o formato blob para geração do arquivo. **/
        let file = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        /** Cria um endereçamento para o arquivo. **/
        let url = window.URL.createObjectURL(file);
        /** Cria um elemento para a linkagem do arquivo. **/
        let link = document.createElement('a');
        /** Adiciona a url no elemento de linkagem. **/
        link.href = url;
        /**  Nomeia o arquivo que será feito o download. **/
        link.download = `${new Date().toLocaleDateString()}-${new Date().getHours()}${new Date().getMinutes()}${new Date().getSeconds()}-Schedulings.csv`;
        /** Adiciona o link no documento. **/
        document.body.appendChild(link);
        /** Atica o evento de click para realizar o download. **/
        link.click();
        /** Remove o link do documento. **/
        document.body.removeChild(link);
        /**  Remove o endereço do download. **/
        window.URL.revokeObjectURL(url);

        this.FILE_DATA = [];
        this.isReady = false;
    }

    /** Função que realiza o download dos dados da tabela em PDF */
    public downloadPDF() {
        let tableLayout: any[];

        let schedulings: any = [];

        tableLayout = [
            'Ligar',
            'Desligar',
            'Dimerização',
            'Status',
            'Instalações',
            'Sucesso',
            'Falha',
            'Criado por',
            'Criado em',
        ];
        this.FILE_DATA.forEach((scheduling) => {
            schedulings.push([
                scheduling?.switchOnTime,
                scheduling?.switchOffTime,
                scheduling?.dimming,
                scheduling?.status,
                scheduling?.totalInstallations,
                scheduling?.successCount,
                scheduling?.failCount,
                scheduling?.createdBy,
                scheduling?.createdAt,
            ]);
        });

        // Define a orientação da tabela (paisagem nesse caso)
        const orientation = 'l';
        // Cria o objeto da lib jsPDF com a orientação e tamanho da página
        const doc: any = new jsPDF(orientation, 'mm', 'a4');

        // Cria a tabela com o tableLayout como header e a a lista de conteúdo como corpo da tabela
        autoTable(doc, {
            theme: 'grid',
            margin: { top: 20 },
            head: [tableLayout],
            body: schedulings,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [63, 81, 181], textColor: 255, halign: 'center' },
        });
        // Baixa o arquivo para o usuário, definindo o nome com a data de download
        doc.save(
            `${new Date().toLocaleDateString()}-${new Date().getHours()}${new Date().getMinutes()}${new Date().getSeconds()}-schedulings.pdf`
        );

        this.FILE_DATA = [];
        this.isReady = false;
    }
}
